import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useNotifications, type Notification } from "@/hooks/useNotifications";

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffSec = Math.floor((now - then) / 1000);

  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function notificationIcon(type: Notification["type"]): { icon: string; color: string } {
  switch (type) {
    case "project_approved":
      return { icon: "fa-check-circle", color: "text-green-400" };
    case "project_rejected":
      return { icon: "fa-times-circle", color: "text-red-400" };
    case "project_submitted":
      return { icon: "fa-clock", color: "text-amber-400" };
    default:
      return { icon: "fa-bell", color: "text-gray-400" };
  }
}

export default function NotificationBell({ textClassName = "text-gray-300" }: { textClassName?: string }) {
  const { profile } = useAuth();
  const { count, notifications, pendingProjects, loading, markAsRead, markAllAsRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isAdmin = profile?.role === "admin";

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleMarkAll = async () => {
    await markAllAsRead();
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    setOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className={`relative w-9 h-9 rounded-full flex items-center justify-center transition-colors hover:bg-white/10 cursor-pointer ${textClassName}`}
        aria-label={`Notifications${count > 0 ? ` (${count} unread)` : ""}`}
      >
        <i className={`fas fa-bell text-base ${open ? "text-[#5cb85c]" : ""}`} />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-[#0d1117] border border-gray-700 rounded-xl shadow-2xl overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
            <h3 className="text-sm font-bold text-gray-200">
              {isAdmin ? "Pending Review" : "Notifications"}
            </h3>
            {!isAdmin && count > 0 && (
              <button
                onClick={handleMarkAll}
                className="text-[11px] text-[#5cb85c] hover:text-[#4cae4c] font-semibold transition-colors cursor-pointer"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Content */}
          <div className="max-h-[360px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <i className="fas fa-spinner fa-spin text-[#5cb85c] text-sm" />
              </div>
            ) : isAdmin ? (
              // Admin: show pending projects
              pendingProjects.length === 0 ? (
                <div className="flex flex-col items-center py-10 px-4">
                  <div className="w-12 h-12 rounded-full bg-green-900/20 flex items-center justify-center mb-3">
                    <i className="fas fa-check-circle text-green-400 text-lg" />
                  </div>
                  <p className="text-sm text-gray-400 text-center">All caught up!</p>
                  <p className="text-xs text-gray-500 text-center mt-1">No projects pending review.</p>
                </div>
              ) : (
                pendingProjects.map((project) => (
                  <Link
                    key={project.id}
                    to={`/admin/projects`}
                    onClick={() => setOpen(false)}
                    className="flex items-start gap-3 px-4 py-3 border-b border-gray-800 hover:bg-[#161b22] transition-colors cursor-pointer group"
                  >
                    <div className="w-8 h-8 rounded-full bg-amber-900/20 border border-amber-700/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="fas fa-clock text-amber-400 text-xs" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-200 truncate group-hover:text-[#5cb85c] transition-colors">
                        {project.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {project.category || "Uncategorized"} &middot; {timeAgo(project.created_at)}
                      </p>
                    </div>
                    <span className="text-[10px] font-semibold bg-amber-900/30 text-amber-400 px-2 py-0.5 rounded-full flex-shrink-0">
                      Pending
                    </span>
                  </Link>
                ))
              )
            ) : (
              // User: show notifications
              notifications.length === 0 ? (
                <div className="flex flex-col items-center py-10 px-4">
                  <div className="w-12 h-12 rounded-full bg-[#1c2128] flex items-center justify-center mb-3">
                    <i className="fas fa-bell-slash text-gray-500 text-lg" />
                  </div>
                  <p className="text-sm text-gray-400 text-center">No notifications yet</p>
                  <p className="text-xs text-gray-500 text-center mt-1">We&apos;ll notify you about your project status here.</p>
                </div>
              ) : (
                notifications.map((notif) => {
                  const { icon, color } = notificationIcon(notif.type);
                  return (
                    <Link
                      key={notif.id}
                      to="/dashboard"
                      onClick={() => handleNotificationClick(notif)}
                      className={`flex items-start gap-3 px-4 py-3 border-b border-gray-800 transition-colors cursor-pointer ${
                        notif.is_read ? "opacity-60 hover:opacity-80" : "bg-[#161b22]/60 hover:bg-[#161b22]"
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        notif.type === "project_approved"
                          ? "bg-green-900/20 border border-green-700/30"
                          : notif.type === "project_rejected"
                          ? "bg-red-900/20 border border-red-700/30"
                          : "bg-amber-900/20 border border-amber-700/30"
                      }`}>
                        <i className={`fas ${icon} ${color} text-xs`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-gray-200 truncate">
                            {notif.title}
                          </p>
                          {!notif.is_read && (
                            <span className="w-2 h-2 rounded-full bg-[#5cb85c] flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">
                          {notif.message}
                        </p>
                        <p className="text-[10px] text-gray-500 mt-1">
                          {timeAgo(notif.created_at)}
                        </p>
                      </div>
                    </Link>
                  );
                })
              )
            )}
          </div>

          {/* Footer */}
          {!isAdmin && notifications.length > 0 && (
            <div className="px-4 py-2.5 border-t border-gray-700 bg-[#161b22]/50">
              <Link
                to="/dashboard"
                onClick={() => setOpen(false)}
                className="block text-center text-xs text-[#5cb85c] hover:text-[#4cae4c] font-semibold transition-colors"
              >
                View all in Dashboard
              </Link>
            </div>
          )}
          {isAdmin && pendingProjects.length > 0 && (
            <div className="px-4 py-2.5 border-t border-gray-700 bg-[#161b22]/50">
              <Link
                to="/admin/projects"
                onClick={() => setOpen(false)}
                className="block text-center text-xs text-[#5cb85c] hover:text-[#4cae4c] font-semibold transition-colors"
              >
                Go to Admin Panel
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}